import React, { useMemo } from "react";
import { motion } from "framer-motion";

type QRMode = 'sign-in' | 'voice-assistant';

interface QRCodeGeneratorProps {
  mode?: QRMode;
  email?: string;
  sessionId?: string;
  storeId?: string;
  kioskId?: string;
  voiceAssistantUrl?: string;
  baseUrl?: string;
  size?: number;
  className?: string;
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({
  mode = 'sign-in',
  email = "",
  sessionId,
  storeId = "lululemon-demo",
  kioskId = "pin-001",
  voiceAssistantUrl = "https://lululemon.voice.pentatonic.com",
  baseUrl = window.location.origin,
  size = 200,
  className = ""
}) => {
  // Generate session ID if not provided
  const activeSessionId = useMemo(() => {
    return sessionId || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, [sessionId]);

  // Generate the URL based on mode
  const targetUrl = useMemo(() => {
    if (mode === 'voice-assistant') {
      const params = new URLSearchParams({
        session: activeSessionId,
        store: storeId,
        kiosk: kioskId,
      });
      return `${voiceAssistantUrl}?${params.toString()}`;
    } else {
      // Original sign-in mode
      return email 
        ? `${baseUrl}/sign-in?email=${encodeURIComponent(email)}`
        : `${baseUrl}/sign-in`;
    }
  }, [mode, activeSessionId, storeId, kioskId, voiceAssistantUrl, baseUrl, email]);

  // Generate QR code URL
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(targetUrl)}&color=1A1A1A&bgcolor=FFFFFF`;

  // Mode-specific content
  const content = mode === 'voice-assistant' ? {
    title: "Talk to your AI Assistant",
    subtitle: "Scan to connect your phone",
    instruction: "Open your camera and scan this code to start a voice conversation",
  } : {
    title: "Scan to Sign In",
    subtitle: email ? `Pre-filled with: ${email}` : undefined,
    instruction: "Open your phone camera and point it at this code",
  };

  return (
    <motion.div
      className={`flex flex-col items-center p-4 ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <div 
        className="bg-white p-4 rounded-2xl shadow-lg"
        style={{ width: size + 32, height: size + 32 }}
      >
        <img
          src={qrCodeUrl}
          alt={`QR Code for ${mode === 'voice-assistant' ? 'Voice Assistant' : 'Sign In'}`}
          width={size}
          height={size}
          className="w-full h-full object-contain"
        />
      </div>
      
      <div className="mt-6 text-center">
        <p className="font-brand-primary font-medium text-[28px] text-brand-text-primary mb-2">
          {content.title}
        </p>
        {content.subtitle && (
          <p className="font-brand-primary font-light text-[18px] text-brand-text-secondary">
            {content.subtitle}
          </p>
        )}
        <p className="font-brand-primary font-light text-[16px] text-brand-text-muted mt-3 max-w-[300px]">
          {content.instruction}
        </p>
        
        {mode === 'voice-assistant' && (
          <div className="mt-4 flex items-center justify-center gap-2 text-brand-text-muted">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
            </svg>
            <span className="text-[14px]">Voice-enabled AI assistant</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default QRCodeGenerator;
